import React from 'react';
import { motion } from 'framer-motion';
import { PhoneCall, CalendarPlus, PhoneForwarded } from 'lucide-react';

export interface ActivityMetrics {
  totalDays: number;
  plannedDays: number;
  callsPerDay: number;
  rdvsPerDay: number;
  reachabilityRate: number;
}

interface Props {
  metrics: ActivityMetrics;
  loading: boolean;
}

const StatisticsActivityCards: React.FC<Props> = ({ metrics, loading }) => {
  if (loading) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {[...Array(3)].map((_, i) => (
          <div key={i} className="bg-gray-50 rounded-lg p-4 border border-gray-100 animate-pulse">
            <div className="flex items-center justify-between mb-3">
              <div className="h-3 bg-gray-200 rounded w-20" />
              <div className="h-8 w-8 bg-gray-200 rounded-lg" />
            </div>
            <div className="h-7 bg-gray-200 rounded w-16 mb-2" />
            <div className="h-3 bg-gray-100 rounded w-24" />
          </div>
        ))}
      </div>
    );
  }

  const cards = [
    {
      label: 'Appels / jour',
      value: metrics.callsPerDay.toFixed(0),
      subValue: 'Moyenne par session',
      icon: PhoneCall,
      iconBg: 'bg-blue-50',
      iconColor: 'text-blue-600',
    },
    {
      label: 'RDV / jour',
      value: metrics.rdvsPerDay.toFixed(1),
      subValue: 'Moyenne par session',
      icon: CalendarPlus,
      iconBg: 'bg-rose-50',
      iconColor: 'text-rose-500',
    },
    {
      label: 'Taux de joignabilite',
      value: `${metrics.reachabilityRate.toFixed(0)}%`,
      subValue: 'Conversations / Appels',
      icon: PhoneForwarded,
      iconBg: 'bg-emerald-50',
      iconColor: 'text-emerald-600',
    },
  ];

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
      {cards.map((card, index) => {
        const Icon = card.icon;
        const showProgress = card.progress !== undefined;

        return (
          <motion.div
            key={card.label}
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.3 + index * 0.05 }}
            className="bg-gray-50 rounded-lg p-4 border border-gray-100 hover:shadow-sm transition-shadow"
          >
            <div className="flex items-center justify-between mb-3">
              <p className="text-xs font-medium text-gray-500">{card.label}</p>
              <div className={`${card.iconBg} ${card.iconColor} p-1.5 rounded-lg`}>
                <Icon className="h-4 w-4" />
              </div>
            </div>

            <p className="text-2xl font-bold text-gray-900 mb-1">{card.value}</p>
            <p className="text-xs text-gray-400">{card.subValue}</p>

            {showProgress && (
              <div className="mt-3">
                <div className="w-full bg-gray-200 rounded-full h-1.5">
                  <div
                    className="bg-slate-600 h-1.5 rounded-full transition-all duration-300"
                    style={{ width: `${Math.min(100, card.progress!)}%` }}
                  />
                </div>
              </div>
            )}
          </motion.div>
        );
      })}
    </div>
  );
};

export default StatisticsActivityCards;
